/**
 * Pure model for the start-new-season flow: every copy string, the gate
 * resolver, the title validator, and the request payload builder. No React,
 * no DOM, no network, so every state is testable without a renderer.
 *
 * `resolveSeasonGate`'s guard order mirrors the server's exact guard order
 * at `postSeasonStartHandler/index.ts` (readiness first, then unsettled) so
 * the pre-flight state and the server verdict never disagree
 * (`plan.md` `## 0.5`).
 */

// Derived from the generated season reads rather than restated here, so
// these shapes cannot drift from the contract.
import type {
  useSeasonList,
  useSeasonStartOptions,
} from "@sportsmet/contest-sdk/private";

export type typeSeasonStartOptions = NonNullable<
  NonNullable<ReturnType<typeof useSeasonStartOptions>["data"]>["data"]
>;
export type typeSeasonListItem = NonNullable<
  NonNullable<ReturnType<typeof useSeasonList>["data"]>["data"]
>["items"][number];

export const SEASON_TITLE_MAX_LENGTH = 40;

export const SEASON_CARD_NO_SEASON_LABEL = "No season started yet";
export const SEASON_CARD_START_LABEL = "Start new season";
export const GATE_PROVIDER_HEADLINE = "We can't start next season yet";
export const GATE_PROVIDER_BODY =
  "Next season's schedule hasn't been published yet. We'll open this as soon as it is.";
export const GATE_UNSETTLED_HEADLINE = "Finish this season first";
export const GATE_UNSETTLED_ACTION_LABEL = "Go to contest";
export const PICKER_TITLE = "Pick the season";
export const PICKER_YEAR_LABEL = "Season";
export const PICKER_TITLE_LABEL = "Title";
export const PICKER_CONTINUE_LABEL = "Continue";
export const CONFIRM_TITLE = "Start a new season?";
export const CONFIRM_BODY =
  "Points, medals and trophies carry over. Picks and results from this season don't.";
export const CANCEL_LABEL = "Cancel";

export function gateUnsettledBody(input: { contestName: string }): string {
  return `${input.contestName} hasn't been settled. You can start a new season once it is.`;
}

export function titleCounterLabel(input: {
  used: number;
  max: number;
}): string {
  return `${input.used}/${input.max}`;
}

export type typeSeasonGate =
  | { kind: "ready" }
  | { kind: "providerNotReady" }
  | { kind: "unsettledContest"; contestId: string; contestName: string };

export function resolveSeasonGate(input: {
  providerDataReady: boolean;
  unsettledContest:
    { contestId: string; contestName: string } | null | undefined;
}): typeSeasonGate {
  if (!input.providerDataReady) {
    return { kind: "providerNotReady" };
  }
  if (input.unsettledContest) {
    return {
      kind: "unsettledContest",
      contestId: input.unsettledContest.contestId,
      contestName: input.unsettledContest.contestName,
    };
  }
  return { kind: "ready" };
}

export function currentSeasonLabel(
  items: readonly typeSeasonListItem[],
): string {
  const current = items.find((item) => item.isCurrent);
  return current ? current.seasonTitle : SEASON_CARD_NO_SEASON_LABEL;
}

export type typeTitleValidity =
  | { isValid: true; trimmed: string }
  | { isValid: false; reason: "empty" | "tooLong" };

export function validateSeasonTitle(raw: string): typeTitleValidity {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { isValid: false, reason: "empty" };
  }
  if (trimmed.length > SEASON_TITLE_MAX_LENGTH) {
    return { isValid: false, reason: "tooLong" };
  }
  return { isValid: true, trimmed };
}

export function buildSeasonStartPayload(input: {
  poolId: string;
  seasonYear: number;
  seasonTitle: string;
}): { poolId: string; seasonYear: number; seasonTitle: string } {
  return {
    poolId: input.poolId,
    seasonYear: input.seasonYear,
    seasonTitle: input.seasonTitle.trim(),
  };
}

// Same shape and reasoning as `conflictTreatment` in
// `apps/app/src/features/poolRevive/reviveSheetModel.ts`: an unrecognized
// code, including a future one, falls through to `null` rather than
// rendering a raw error code or reason to a fan.
export function startFailureNote(code: string | null): string | null {
  switch (code) {
    case "PROVIDER_SEASON_DATA_MISSING":
      return GATE_PROVIDER_BODY;
    case "UNSETTLED_CONTEST_EXISTS":
      return GATE_UNSETTLED_HEADLINE;
    default:
      return null;
  }
}
